// === INPACT:SLUITSTRAAT:BOILERPLATE:SLUITSTRAAT-INFO-PAGE:v1 ===

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  useConfig,
  updateSluitstraatConfig,
  SettingsHeader,
  SettingsFooter,
} from '@sluitstraat/core';

interface SluitstraatFormData {
  environment: 'development' | 'staging' | 'production';
  version: string;
  appName: string;
}

interface FormErrors {
  appName?: string;
}

const SluitstraatInformationPage: React.FC = () => {
  const { config, reloadConfig } = useConfig();
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [notification, setNotification] = useState<{ type: 'success' | 'error'; message: string } | null>(null);

  const [formData, setFormData] = useState<SluitstraatFormData>({
    environment: (config.environment as SluitstraatFormData['environment']) || 'development',
    version: config.version || '1.0',
    appName: config.branding?.appName || 'Inpact Sluitstraat',
  });

  const [errors, setErrors] = useState<FormErrors>({});

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.appName.trim()) {
      newErrors.appName = 'Application name is required';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      setNotification({ type: 'error', message: 'Please fix the errors before saving' });
      return;
    }

    setIsSubmitting(true);
    setNotification(null);

    try {
      const success = await updateSluitstraatConfig(formData);

      if (success) {
        setNotification({ type: 'success', message: 'Sluitstraat information saved successfully!' });

        // Reload the config to reflect the changes
        try {
          await reloadConfig();
        } catch (configError) {
          // Continue with navigation even if reload fails
        }

        // Navigate back to setup page
        setTimeout(() => {
          navigate('/settings', { replace: true });
        }, 1500);
      } else {
        setNotification({ type: 'error', message: 'Failed to save information. Please try again.' });
      }
    } catch (error) {
      setNotification({ type: 'error', message: 'An error occurred while saving. Please try again.' });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/settings');
  };

  return (
    <>
      <SettingsHeader />
      <div style={styles.container}>
        <div style={styles.content}>
        <header style={styles.header}>
          <button onClick={handleCancel} style={styles.backButton}>
            ← Back to Setup
          </button>
          <h1 style={styles.title}>Sluitstraat Information</h1>
          <p style={styles.subtitle}>Configure your instance settings and environment</p>
        </header>

        {notification && (
          <div style={{
            ...styles.notification,
            ...(notification.type === 'success' ? styles.notificationSuccess : styles.notificationError)
          }}>
            <span style={styles.notificationIcon}>
              {notification.type === 'success' ? '✓' : '⚠'}
            </span>
            {notification.message}
          </div>
        )}

        <form onSubmit={handleSubmit} style={styles.form}>
          <div style={styles.section}>
            <h2 style={styles.sectionTitle}>Application Settings</h2>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Application Name <span style={styles.required}>*</span>
              </label>
              <input
                type="text"
                value={formData.appName}
                onChange={(e) => setFormData({ ...formData, appName: e.target.value })}
                style={{
                  ...styles.input,
                  ...(errors.appName ? styles.inputError : {})
                }}
                placeholder="e.g., VeloVerzeker Portal"
              />
              {errors.appName && <span style={styles.errorText}>{errors.appName}</span>}
              <p style={styles.hint}>The name displayed in the browser tab and header.</p>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>
                Environment <span style={styles.required}>*</span>
              </label>
              <select
                value={formData.environment}
                onChange={(e) => setFormData({ ...formData, environment: e.target.value as SluitstraatFormData['environment'] })}
                style={styles.select}
              >
                <option value="development">Development</option>
                <option value="staging">Staging</option>
                <option value="production">Production</option>
              </select>
              <p style={styles.hint}>Select the environment for this instance.</p>
            </div>

            <div style={styles.formGroup}>
              <label style={styles.label}>Version</label>
              <input
                type="text"
                value={formData.version}
                onChange={(e) => setFormData({ ...formData, version: e.target.value })}
                style={styles.input}
                placeholder="e.g., 1.0.0"
              />
              <p style={styles.hint}>Version number for tracking releases.</p>
            </div>
          </div>

          <div style={styles.infoBox}>
            <div style={styles.infoBoxIcon}>ℹ️</div>
            <div>
              <h3 style={styles.infoBoxTitle}>Configuration Impact</h3>
              <ul style={styles.infoBoxList}>
                <li>The <strong>Application Name</strong> appears in the browser tab and header</li>
                <li>The <strong>Environment</strong> setting affects logging, error handling, and UI badges</li>
                <li>The <strong>Version</strong> number helps track releases and updates</li>
                <li>These settings are stored in <code>config.json</code> and reflected immediately</li>
              </ul>
            </div>
          </div>

          <div style={styles.actionBar}>
            <button
              type="button"
              onClick={handleCancel}
              style={styles.cancelButton}
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="submit"
              style={{
                ...styles.saveButton,
                ...(isSubmitting ? styles.saveButtonDisabled : {})
              }}
              disabled={isSubmitting}
            >
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
    <SettingsFooter />
  </>
  );
};

const styles = {
  container: {
    minHeight: '100vh',
    backgroundColor: '#F5F5F5',
    padding: '32px 20px',
  },
  content: {
    maxWidth: '900px',
    margin: '0 auto',
  },
  header: {
    marginBottom: '32px',
  },
  backButton: {
    padding: '8px 16px',
    backgroundColor: 'transparent',
    color: '#6b7280',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    marginBottom: '16px',
    transition: 'color 0.2s',
  },
  title: {
    margin: '0 0 8px 0',
    fontSize: '32px',
    fontWeight: '700' as const,
    color: '#1f2937',
  },
  subtitle: {
    margin: 0,
    fontSize: '16px',
    color: '#6b7280',
  },
  notification: {
    padding: '16px 20px',
    borderRadius: '8px',
    marginBottom: '24px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    fontSize: '14px',
    fontWeight: '600' as const,
  },
  notificationSuccess: {
    backgroundColor: '#d1fae5',
    color: '#065f46',
    border: '2px solid #10b981',
  },
  notificationError: {
    backgroundColor: '#fee2e2',
    color: '#991b1b',
    border: '2px solid #ef4444',
  },
  notificationIcon: {
    fontSize: '18px',
    fontWeight: '700' as const,
  },
  form: {
    backgroundColor: 'white',
    borderRadius: '12px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    padding: '32px',
  },
  section: {
    marginBottom: '32px',
    paddingBottom: '32px',
    borderBottom: '1px solid #e5e7eb',
  },
  sectionTitle: {
    margin: '0 0 20px 0',
    fontSize: '18px',
    fontWeight: '600' as const,
    color: '#1f2937',
  },
  formGroup: {
    marginBottom: '20px',
  },
  label: {
    display: 'block',
    marginBottom: '8px',
    fontSize: '14px',
    fontWeight: '600' as const,
    color: '#374151',
  },
  required: {
    color: '#ef4444',
  },
  input: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
  },
  select: {
    width: '100%',
    padding: '10px 14px',
    border: '1px solid #d1d5db',
    borderRadius: '6px',
    fontSize: '14px',
    fontFamily: 'inherit',
    transition: 'border-color 0.2s',
    boxSizing: 'border-box' as const,
    backgroundColor: '#ffffff',
    color: '#1f2937',
  },
  inputError: {
    borderColor: '#ef4444',
  },
  errorText: {
    display: 'block',
    marginTop: '6px',
    fontSize: '13px',
    color: '#ef4444',
  },
  hint: {
    margin: '6px 0 0 0',
    fontSize: '13px',
    color: '#6b7280',
    fontStyle: 'italic' as const,
  },
  infoBox: {
    backgroundColor: '#eff6ff',
    border: '2px solid #3b82f6',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '32px',
    display: 'flex',
    gap: '16px',
  },
  infoBoxIcon: {
    fontSize: '24px',
    flexShrink: 0,
  },
  infoBoxTitle: {
    margin: '0 0 12px 0',
    fontSize: '16px',
    fontWeight: '600' as const,
    color: '#1e40af',
  },
  infoBoxList: {
    margin: '0',
    paddingLeft: '20px',
    fontSize: '14px',
    color: '#1e40af',
    lineHeight: '1.6',
  },
  actionBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    gap: '12px',
    paddingTop: '24px',
    marginTop: '8px',
  },
  cancelButton: {
    padding: '12px 24px',
    backgroundColor: '#f3f4f6',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
  saveButton: {
    padding: '12px 32px',
    backgroundColor: 'var(--primary-color, #056666)',
    color: 'white',
    border: 'none',
    borderRadius: '6px',
    fontSize: '14px',
    fontWeight: '600' as const,
    cursor: 'pointer',
    transition: 'opacity 0.2s',
  },
  saveButtonDisabled: {
    opacity: 0.6,
    cursor: 'not-allowed',
  },
};

export default SluitstraatInformationPage;
